import { cx } from '@emotion/css';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { ix } from '../../../../../../utils/ix';
import { Checkbox, CircularProgress } from '../../../../../atoms';
import { SelectDropdownField } from '../../../../formFields';
import { SearchInput } from '../SearchInput';
import { LIST_MAX_HEIGHT_PIXELS, MIN_LIST_WIDTH_PIXELS, OVERSCAN_COUNT, ROW_SIZE_PIXELS, SIGN_WIDTH_PIXELS, } from './MenuContent.constants';
import { useStyles } from './MenuContent.styles';
import { addCustomValues, splitItems } from './MenuContent.utils';
export const MenuContent = memo(({ className, hideSelectAll, operatorOptions, operator, options, values, isOpen, id, isLoading, label, onValuesChange, onOperatorChange, }) => {
    const [search, setSearch] = useState('');
    const classes = useStyles();
    const defaultHideSelectAllValue = !(operator === 'equals' || operator === 'does not equal');
    const shouldHideSelectAll = hideSelectAll !== null && hideSelectAll !== void 0 ? hideSelectAll : defaultHideSelectAllValue;
    useEffect(() => {
        if (!isOpen) {
            setSearch('');
        }
    }, [isOpen]);
    const preparedOptionFromOperator = useMemo(() => {
        return { value: operator, label: operator };
    }, [operator]);
    const optionsWithCustomValues = useMemo(() => {
        return addCustomValues(options, values);
    }, [options, values]);
    const [allCheckedItems] = useMemo(() => {
        return splitItems(optionsWithCustomValues, values);
    }, [optionsWithCustomValues, values]);
    const filteredOptions = useMemo(() => {
        return optionsWithCustomValues.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
    }, [optionsWithCustomValues, search]);
    const [filteredCheckedItems, filteredUncheckedItems] = useMemo(() => {
        return splitItems(filteredOptions, values);
    }, [filteredOptions, values]);
    const handleOperatorChange = useCallback((option) => {
        onOperatorChange(option.value);
    }, [onOperatorChange]);
    const handleOnSearchChange = useCallback((value) => {
        setSearch(value);
    }, []);
    const handleSelectAllClick = () => {
        if (optionsWithCustomValues.some((option) => values.includes(option.value))) {
            onValuesChange([]);
        }
        else {
            onValuesChange(filteredOptions.map((option) => option.value));
        }
    };
    const handleItemClick = (clickedValue) => {
        if (values.includes(clickedValue)) {
            onValuesChange(values.filter((checkedValue) => checkedValue !== clickedValue));
        }
        else {
            onValuesChange([...values, clickedValue]);
        }
    };
    const handleAddOptionClick = () => {
        onValuesChange([...values, search]);
        setSearch('');
    };
    const showNoOptions = optionsWithCustomValues.length === 0 && search.length === 0;
    const renderListItem = ({ data, index, style }) => {
        const { label: valueLabel, value, type } = data[index];
        if (type === 'divider') {
            return (React.createElement(Box, { style: style, key: index, className: classes.listDivider },
                React.createElement(Divider, null)));
        }
        return (React.createElement(Box, { id: ix(id, 'filter-option', value), key: index, style: style, className: classes.listItem, onClick: () => handleItemClick(value) },
            React.createElement(Checkbox, { id: ix(id, 'filter-option-checkbox', value), className: classes.checkbox, checked: type === 'checked' }),
            React.createElement("span", { id: ix(id, 'filter-option-label', value), className: classes.label }, valueLabel)));
    };
    const renderList = () => {
        if (filteredCheckedItems.length === 0 && filteredUncheckedItems.length === 0) {
            return null;
        }
        const dividerList = filteredCheckedItems.length > 0 && filteredUncheckedItems.length > 0
            ? [{ value: '', label: '', type: 'divider' }]
            : [];
        const items = [...filteredCheckedItems, ...dividerList, ...filteredUncheckedItems];
        const width = Math.max(...items.map((item) => item.label.length * SIGN_WIDTH_PIXELS), MIN_LIST_WIDTH_PIXELS);
        const height = Math.min(ROW_SIZE_PIXELS * items.length, LIST_MAX_HEIGHT_PIXELS);
        return (React.createElement(FixedSizeList, { height: height, width: width, overscanCount: OVERSCAN_COUNT, itemData: items, itemCount: items.length, itemKey: (index, data) => data[index].value, itemSize: ROW_SIZE_PIXELS }, renderListItem));
    };
    return (React.createElement(Box, { id: ix(id, 'container'), className: cx(className, classes.root, { isOpen }) },
        React.createElement(SearchInput, { id: ix(id, 'search'), placeholder: label, value: search, onChange: handleOnSearchChange }),
        React.createElement(Divider, { className: classes.divider }),
        React.createElement(SelectDropdownField, { id: ix(id, 'operator-select'), className: classes.operatorSelect, value: preparedOptionFromOperator, options: operatorOptions, onChange: handleOperatorChange }),
        !isLoading && (React.createElement(React.Fragment, null,
            search.length > 0 && (React.createElement(Box, { id: ix(id, 'create'), className: classes.createOption, onClick: handleAddOptionClick },
                "Create \u201C",
                search,
                "\u201D")),
            showNoOptions && (React.createElement("span", { id: ix(id, 'no-options'), className: classes.noOptions }, "No options")),
            React.createElement(Box, null,
                !shouldHideSelectAll && filteredOptions.length > 0 && (React.createElement(Box, { id: ix(id, 'select-all'), className: classes.selectAll, onClick: handleSelectAllClick },
                    React.createElement(Checkbox, { id: ix(id, 'select-all-checkbox'), className: classes.checkbox, checked: allCheckedItems.length === optionsWithCustomValues.length, indeterminate: allCheckedItems.length > 0 && allCheckedItems.length < optionsWithCustomValues.length }),
                    React.createElement("span", null, "Select All"))),
                renderList()))),
        isLoading && (React.createElement(Box, { className: classes.loadingIndicator },
            React.createElement(CircularProgress, null)))));
});
//# sourceMappingURL=MenuContent.js.map